import {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import {
  ApiOrganization,
  ApiUserProfile,
  changeUserRole,
  deleteUser,
  fetchOrganizationUsers,
  updateUser,
} from 'src/lib/api/auth';

import {patchCachedUserProfile} from './useUserProfile';

function makeUsersQueryKey(organizationId: ApiOrganization['id']) {
  return ['organization', organizationId, 'users'];
}

/**
 * Returns the user profiles for the given organization.
 */
export default function useOrganizationUsersQuery(
  organizationId: ApiOrganization['id']
): UseQueryResult<ApiUserProfile[]> {
  return useQuery(makeUsersQueryKey(organizationId), () => {
    return fetchOrganizationUsers(organizationId);
  });
}

export type UpdatableUserFields = Pick<ApiUserProfile, 'name'>;

export function useUserUpdateMutation(
  user: ApiUserProfile,
  organizationId: ApiOrganization['id']
): UseMutationResult<ApiUserProfile, Error, UpdatableUserFields, () => void> {
  const queryClient = useQueryClient();

  return useMutation(
    (userUpdateData: UpdatableUserFields) => {
      return updateUser(user.id, userUpdateData);
    },
    {
      onSuccess: (data: ApiUserProfile) => {
        updateUserInQueryClient(queryClient, organizationId, {...user, ...data});
      },
    }
  );
}

export function useUserChangeRoleMutation(
  user: ApiUserProfile,
  organizationId: ApiOrganization['id']
): UseMutationResult<ApiUserProfile, Error, ApiUserProfile['role'], () => void> {
  const queryClient = useQueryClient();

  return useMutation(
    (role: ApiUserProfile['role']) => {
      return changeUserRole(user.id, role);
    },
    {
      onSuccess: (data: ApiUserProfile) => {
        updateUserInQueryClient(queryClient, organizationId, {...user, ...data});
      },
    }
  );
}

export function useUserDeleteMutation(
  user: ApiUserProfile
): UseMutationResult<ApiUserProfile, Error, null, () => void> {
  return useMutation(
    () => {
      return deleteUser(user.id);
    },
    {
      onSuccess: () => {
        // replace everything in the URL starting from "/users/" up to then end
        // with just "/users", thus moving back from user edit form page to
        // users list. The full-page navigation discards the query cache, so no
        // cache updates are needed here.
        window.location.href = window.location.href.replace(/\/users\/.*?$/, '/users');
      },
    }
  );
}

// The edited user may be the signed-in user, so sync their name/role into the
// userProfile query that AuthProvider consumes.
function updateUserInQueryClient(
  queryClient: QueryClient,
  organizationId: ApiOrganization['id'],
  user: ApiUserProfile
) {
  const key = makeUsersQueryKey(organizationId);
  const users = queryClient.getQueryData<ApiUserProfile[]>(key);
  if (users) {
    queryClient.setQueryData(
      key,
      users.map((u) => (u.id === user.id ? user : u))
    );
  }
  queryClient.invalidateQueries(key);

  patchCachedUserProfile(queryClient, (profile) =>
    profile.id === user.id ? {...profile, name: user.name, role: user.role} : profile
  );
}
